import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

export interface NextBestActionResult {
  leadId?: number;
  recommendationId?: number;
  leadStage: string;
  leadScoreCategory: 'HOT' | 'WARM' | 'COLD' | 'NEW' | 'STALE';
  recommendedAction: string;
  confidence: 'HIGH' | 'MEDIUM' | 'LOW' | number;
  reasoning: string;
  keySignals: string[];
  riskFactors: string[];
  alternativeAction: string;
  explanationSummary: string;

  // Upgraded operational fields
  executionPlan?: { step: number; action: string }[];
  recommendedTemplate?: string;
  nextReviewDays?: number;
  automationSuggested?: boolean;

  // Backwards compatibility fields
  actionType: string;
  actionText: string;
  explanation: string;
  confidenceScore?: number;
  source?: string;
  generatedAt?: string;
}

export type NbaFeedback = 'ACCEPTED' | 'IGNORED' | 'EXECUTED';

@Injectable({
  providedIn: 'root'
})
export class NextBestActionService {

  private readonly baseUrl = `${environment.serverAPI9}/api/v1/ai/recommendations`;

  /** Cache: leadId → current recommendation */
  private cache = new Map<number, NextBestActionResult>();

  /** Loading state per lead */
  private loadingSubject = new BehaviorSubject<boolean>(false);
  loading$ = this.loadingSubject.asObservable();

  constructor(private http: HttpClient) { }

  /**
   * Fetch the latest ACTIVE recommendation (fast, no LLM call).
   * Returns null observable if none exists.
   */
  getLatest(leadId: number): Observable<NextBestActionResult | null> {
    if (this.cache.has(leadId)) {
      return of(this.cache.get(leadId));
    }
    return this.http.get<NextBestActionResult>(`${this.baseUrl}/leads/${leadId}/latest`).pipe(
      tap(result => {
        if (result) this.cache.set(leadId, result);
      }),
      catchError(() => of(null))
    );
  }

  /**
   * Generate a fresh recommendation (calls LLM — shows spinner).
   */
  generate(leadId: number): Observable<NextBestActionResult> {
    this.loadingSubject.next(true);
    return this.http.post<NextBestActionResult>(
      `${this.baseUrl}/leads/${leadId}/generate`, {}
    ).pipe(
      tap(result => {
        this.cache.set(leadId, result);
        this.loadingSubject.next(false);
      }),
      catchError(err => {
        this.loadingSubject.next(false);
        throw err;
      })
    );
  }

  /**
   * Submit sales rep feedback for a recommendation.
   */
  submitFeedback(recommendationId: number, feedback: NbaFeedback): Observable<void> {
    return this.http.patch<void>(
      `${this.baseUrl}/${recommendationId}/feedback`,
      { feedback }
    );
  }

  /** Clear cache for a lead (e.g. after generating fresh). */
  clearCache(leadId: number): void {
    this.cache.delete(leadId);
  }
}
